import type {
  AppCategory,
  ListAppRegistryResponse,
  ListInstalledAppsResponse
} from '@bliks-io/contracts';
import { resolveAppLabel } from '@/lib/app-registry';

type RegistryApp = ListAppRegistryResponse['apps'][number];
type InstalledApp = ListInstalledAppsResponse['apps'][number];

export interface MergedAppRow {
  appId: string;
  name: string;
  /** Already resolved for the requested locale — a locale map in the row data
   *  would survive a language switch. */
  description: string;
  category?: AppCategory;
  /** The icon name the published registry manifest carries. */
  iconName?: string;
  installed: boolean;
  enabled: boolean;
  /** The superadmin's permission flag, normalized by the merge. */
  allowed: boolean;
  /** Only management reads carry the seat state; absent/null cap = unlimited. */
  seatLimit?: number | null;
  assignedUserIds?: string[];
  /** Superadmin-set per-app config; management reads only, shape per app. */
  config?: unknown;
  /** The app manifest's declared scopes; empty until the app is installed. */
  permissions: string[];
}

/** `notAllowed` is the superadmin's permission flag, not the tenant's own switch. */
export type AppConsoleStatus = 'enabled' | 'disabled' | 'notAllowed';

export function appConsoleStatus(app: MergedAppRow): AppConsoleStatus {
  if (!app.allowed) return 'notAllowed';
  return app.enabled ? 'enabled' : 'disabled';
}

export function mergeAppCatalog(
  registryApps: RegistryApp[] | undefined,
  installedApps: InstalledApp[] | undefined,
  locale: string
): MergedAppRow[] {
  if (!registryApps) return [];

  const installedMap = new Map((installedApps ?? []).map((app) => [app.appId, app]));

  return registryApps.map((reg) => {
    const installed = installedMap.get(reg.appId);
    return {
      appId: reg.appId,
      name: reg.name,
      description: resolveAppLabel(reg.description ?? '', locale),
      category: reg.category,
      iconName: reg.icon,
      installed: !!installed,
      enabled: installed?.enabled ?? false,
      // Both callers are management views, where the read omits the flag only
      // for a subdoc written before it existed — and absent means allowed.
      allowed: installed?.allowed ?? true,
      seatLimit: installed?.seatLimit,
      assignedUserIds: installed?.assignedUserIds,
      config: installed?.config,
      permissions: installed?.permissions ?? []
    };
  });
}
